import { GlobalToken, theme } from "antd";
import { CSSProperties, PropsWithChildren, useCallback } from "react";
import { useColorScheme } from "src/hooks/useColorScheme";
import { hexToRgb, hslToHex, rgbToHsl } from "src/utils/color";
import { Patterns, svgImage } from "./types";

export function Background({
  children,
  pattern,
  style = {},
  colorName,
  ...shorthand
}: PropsWithChildren<{
  pattern?: Patterns;
  width?: CSSProperties["width"];
  height?: CSSProperties["height"];
  color?: CSSProperties["color"];
  style?: CSSProperties;
  colorName: string;
}>) {
  const svg = svgImage(pattern);
  const width = shorthand.width || style.width;
  const height = shorthand.height || style.height;
  const color = shorthand.color || style.color;
  const backgroundColor = useThemeColor(colorName);
  const backgroundImage = `url(${svg.src})`;
  return (
    <div
      style={{
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundBlendMode: "overlay",
        ...style,
        backgroundColor,
        backgroundImage,
        color,
        height,
        width,
      }}
    >
      {children}
    </div>
  );
}

function colorize(token: GlobalToken, colorName: string, mode: string) {
  const idx = mode === "light" ? 2 : 6;
  const hex = token[`${colorName}${idx}` as keyof typeof token] as string;
  const [h, s, l] = rgbToHsl(...hexToRgb(hex));
  return hslToHex(h, s * 0.5, l * 0.95);
}

export function useThemeColor(colorName = "green") {
  const { token } = theme.useToken();
  const mode = useColorScheme();
  return colorize(token, colorName, mode);
}

export function useColorThemer() {
  const mode = useColorScheme();
  const { token } = theme.useToken();
  return useCallback(
    (colorName = "green") => colorize(token, colorName, mode),
    [mode, token]
  );
}
